import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import {
  Button,
  Card,
  GroupButton,
  Input,
  Popover
} from '@tecsinapse/cortex-react'
import React from 'react'
import { Roles } from '@/core/enums/roles'
import { AccessControl } from '@/core/utils/access-control'
import { EmptyList } from '@/core/components/empty-list'
import { useUploaderDialog } from '../hooks/use-uploader-dialog'
import { BreadcrumbsPastas } from '../components/breadcrumbs-pastas'
import { TabelaBuscaArquivo } from '../components/tabela-busca-arquivo'
import { TabelaPastas } from '../components/tabela-pastas'
import { UploaderDialog } from '../components/uploader-dialog'
import { useBibliotecaVirtual } from '../hooks/use-biblioteca-virtual'
import { MODE_OPTIONS } from '../utils/view-options'
import { ModeType } from '../utils/constants'
import ToolbarElement from '../components/toolbar-element'
import { NovaPastaDrawer } from '../components/nova-pasta-drawer'
import { useArquivosRecentes } from '../hooks/use-arquivos-recentes'
import { useSelectViewItem } from '../hooks/use-select-view-item'
import { useSelectItem } from '../hooks/use-select-item'
import { Loading } from '@/core/components/loading'
import {
  ItemArquivoBibliotecaCard,
  ItensRecentesDisplay,
  TabelaArquivos
} from '../components'
import { FiFile, FiFolder } from 'react-icons/fi'
import { ItemBiblioteca } from '@/core/types/graphql'
import { PopoverActionItem } from '@/core/components/popover-action-item'
import { BibliotecaViewSlider } from '../components/view-slider'
import { ItemsCount } from '../components/items-count'
import { EventGoal, EventOrigin, logEvent } from '@/core/analytics'
import { AnalyticsEvents } from '@/core/analytics/analytics-eventes-enum'

export const BibliotecaVirtual = () => {
  const {
    searchText,
    setSearchText,
    pasta,
    loadingPasta,
    isSearching,
    searchDebounce,
    handleOpenNewFolder,
    handleCloseNewFolder,
    pastas,
    isRoot,
    loadingPastas,
    hasWritePermission,
    isItemSelected,
    itemsPorPasta,
    pastasOptions,
    generateDownloadLink,
    mode,
    setMode,
    isFolder,
    setIsFolder,
    isNewFolderOpen,
    showItensList,
    showItensModule,
    arquivos,
    isMobile,
    images
  } = useBibliotecaVirtual()

  const { handleClick, handleDoubleClick, resetSelection, selectedRows } =
    useSelectItem({
      setSearchText,
      generateDownloadLink
    })

  const { dataArquivosRecentes, loadingArquivosRecentes } =
    useArquivosRecentes()

  const { selectedViewItem, onCloseViewItem, onSelectViewItem } =
    useSelectViewItem()

  const uploader = useUploaderDialog({
    pastaAtual: pasta as ItemBiblioteca,
    isFolder
  })

  if (!pasta) {
    return <EmptyList />
  }

  return (
    <div>
      <div className='flex flex-1 items-center justify-between mb-deca'>
        <p className='text-h1 text-secondary-xdark font-bold'>{t`Biblioteca Virtual`}</p>
        <div className='space-x-deca flex flex-1 justify-end items-center'>
          {!isMobile ? (
            <Input.Search
              onChange={e => {
                logEvent({
                  name: AnalyticsEvents.BUSCA_BIBLIOTECA_VIRTUAL,
                  params: {
                    origin: EventOrigin.input,
                    goal: EventGoal.filter
                  }
                })
                setSearchText(e.target.value)
              }}
              name='search'
              label={t`Pesquisar arquivos ou pastas`}
              className='w-[35%] overflow-hidden'
              id='searchInputBiblioteca'
              defaultValue={searchText}
            />
          ) : null}
          {hasWritePermission || isRoot ? (
            <AccessControl allowedRoles={[Roles.PORTAL_GERENCIAR_BIBLIOTECA]}>
              <div className='flex gap-3'>
                <Button disabled={isSearching} onClick={handleOpenNewFolder}>
                  <Trans>Nova Pasta</Trans>
                </Button>
                {!isRoot ? (
                  <Popover.Root trigger={'click'}>
                    <Popover.Trigger>
                      <Button disabled={isSearching}>
                        <Trans>Upload</Trans>
                      </Button>
                    </Popover.Trigger>
                    <Popover.Content className='bg-white'>
                      <PopoverActionItem
                        onClick={() => {
                          setIsFolder(false)
                          uploader.onOpen()
                        }}
                      >
                        <FiFile />
                        <p>{t`Upload de Arquivo`}</p>
                      </PopoverActionItem>
                      <PopoverActionItem
                        onClick={() => {
                          setIsFolder(true)
                          uploader.onOpen()
                        }}
                      >
                        <FiFolder />
                        <p>{t`Upload de Pasta`}</p>
                      </PopoverActionItem>
                    </Popover.Content>
                  </Popover.Root>
                ) : null}
              </div>
            </AccessControl>
          ) : null}
          {!isSearching ? (
            <GroupButton
              className={'h-[44px]'}
              customStyles={{
                active: 'bg-primary-medium'
              }}
              options={MODE_OPTIONS}
              disableAllOptions={loadingArquivosRecentes}
              value={MODE_OPTIONS.find(op => op.value.type === mode)?.value}
              onChange={mode => {
                logEvent({
                  name: AnalyticsEvents.EXIBICAO_BIBLIOTECA_VIRTUAL,
                  params: {
                    origin: EventOrigin.button,
                    goal: EventGoal.filter
                  }
                })
                setMode(mode?.type as ModeType)
              }}
              renderKey={mode => String(mode?.type)}
              renderOption={mode => mode?.icon}
            />
          ) : null}
        </div>
      </div>

      {!isSearching ? (
        <BreadcrumbsPastas pasta={pasta as ItemBiblioteca} />
      ) : null}
      {!isSearching && !isRoot ? (
        <ItemsCount itemsPorPasta={itemsPorPasta} />
      ) : null}

      {loadingPasta ? (
        <Loading />
      ) : (
        <>
          {showItensModule && !isSearching ? (
            <ItensRecentesDisplay
              dataArquivosRecentes={dataArquivosRecentes}
              loading={loadingArquivosRecentes}
              isRoot={isRoot}
            />
          ) : !isSearching ? (
            <TabelaArquivos isRoot={isRoot} />
          ) : null}
          <ToolbarElement
            selectedRows={selectedRows}
            resetSelection={resetSelection}
          />
          {showItensModule ? (
            <div data-testid='items-grid'>
              {(pastas ?? []).length > 0 && !isSearching ? (
                <Card className='space-y-kilo mb-deca'>
                  <div className='flex flex-row items-center justify-between'>
                    <p className='text-h3 text-secondary-xdark font-bold'>
                      <Trans>Pastas</Trans>
                    </p>
                  </div>
                  <div className='grid grid-cols-1 sm:grid-cols-3 gap-x-deca gap-y-deca'>
                    {(pastas ?? []).map(pasta => (
                      <ItemArquivoBibliotecaCard
                        key={pasta?.id}
                        item={pasta as ItemBiblioteca}
                        isSelected={isItemSelected(
                          selectedRows,
                          String(pasta?.id)
                        )}
                        onClickItem={handleClick}
                        onSelectItem={handleDoubleClick}
                        onSelectViewItem={onSelectViewItem}
                      />
                    ))}
                  </div>
                </Card>
              ) : !loadingPastas &&
                !((arquivos ?? []).length > 0) &&
                !isSearching ? (
                <EmptyList />
              ) : null}
              {!isRoot && (arquivos ?? []).length > 0 && !isSearching ? (
                <Card className={'space-y-kilo mb-deca'}>
                  <div className='flex justify-between'>
                    <p className='text-h3 text-secondary-xdark font-bold'>
                      <Trans>Arquivos</Trans>
                    </p>
                  </div>
                  {(arquivos ?? []).length > 0 ? (
                    <div className='grid lg:grid-cols-3 grid-cols-1 gap-deca'>
                      {arquivos?.map(itemBiblioteca => (
                        <ItemArquivoBibliotecaCard
                          key={itemBiblioteca?.id}
                          item={itemBiblioteca as ItemBiblioteca}
                          isSelected={isItemSelected(
                            selectedRows,
                            String(itemBiblioteca?.id)
                          )}
                          onClickItem={handleClick}
                          onSelectItem={handleDoubleClick}
                          onSelectViewItem={onSelectViewItem}
                        />
                      ))}
                      <BibliotecaViewSlider
                        itens={images}
                        itemSelected={selectedViewItem}
                        onCloseViewItem={onCloseViewItem}
                      />
                    </div>
                  ) : null}
                </Card>
              ) : null}
            </div>
          ) : null}
          {showItensList && !isSearching ? (
            <TabelaPastas
              itemsPorPasta={itemsPorPasta}
              isItemSelected={isItemSelected}
              onClickItem={handleClick}
              selectedRows={selectedRows}
              onDoubleClick={handleDoubleClick}
            />
          ) : null}
          {isSearching ? (
            <TabelaBuscaArquivo
              selectedRows={selectedRows}
              onClickItem={handleClick}
              onDoubleClick={handleDoubleClick}
              isItemSelected={isItemSelected}
              searchText={searchDebounce}
              pastaAtual={pasta}
            />
          ) : null}
          <UploaderDialog uploader={uploader} isFolder={isFolder} />
          <NovaPastaDrawer
            isOpen={isNewFolderOpen}
            onOpen={handleCloseNewFolder}
            pastaAtual={pasta}
            pastaOptions={pastasOptions}
          />
        </>
      )}
    </div>
  )
}

export default BibliotecaVirtual
